import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEcomV1TotalsCalculation from './ecom-v1-totals-calculation-totals-calculator.http.js';
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

const TRANSFORM_PATHS_0 = [
  { path: 'shippingAddress' },
  { path: 'billingAddress' },
  { path: 'shippingOptions.shippingOptions.logistics.pickupDetails.address' },
];
const TRANSFORM_PATHS_1 = [
  {
    path: 'shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address',
  },
  {
    path: 'shippingInfo.carrierServiceOptions.shippingOptions.logistics.pickupDetails.address',
  },
];

export interface TotalsCalculationEntity {}

export interface CalculateTotalsRequest
  extends CalculateTotalsRequestCouponOneOf,
    CalculateTotalsRequestGiftCardOneOf {
  /** Coupon ID. */
  couponId?: string | null;
  /** Coupon code. */
  couponCode?: string | null;
  /**
   * DEPRECATED: Use gift_cards instead.
   * @deprecated DEPRECATED: Use gift_cards instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardCode?: string | null;
  /**
   * Calculation ID. An identifier of the entity that you are calculating totals for, for example, cart ID or checkout ID.
   * This value will be used in order to optimise calculations by caching.
   * If not passed, calculation might be slower in some cases.
   * @minLength 1
   * @maxLength 100
   */
  calculationId?: string | null;
  /**
   * Line items for calculation.
   * @maxSize 300
   */
  lineItems?: LineItem[];
  /** Shipping address for tax and shipping calculation (if applicable). */
  shippingAddress?: Address;
  /** Billing address. Used for calculating tax if all the items in the cart are not shippable. */
  billingAddress?: Address;
  /** Details about selected shipping option. */
  selectedShippingOption?: SelectedShippingOption;
  /**
   * Buyer email. Used to enforce coupon [single-use per customer](https://support.wix.com/en/article/wix-stores-limiting-coupon-usage). If not passed, single coupon usage per customer will not be enforced.
   * @format EMAIL
   */
  buyerEmail?: string | null;
  /**
   * Whether to calculate tax in the calculation request.
   *
   * Default: `true`
   * @internal
   */
  calculateTax?: boolean | null;
  /**
   * Member id to use for this calculation
   * When not provided, given that the caller is a member, its member id will be used
   * Providing this require elevated permissions - ECOM.ADMIN_CALCULATE_TOTALS
   */
  memberId?: string | null;
  /**
   * The selected memberships and which line items they apply to
   * When not provided, default selection will be returned
   * You can also set it to empty selection
   * Mutually exclusive with `membership_selection` - providing both returns an INVALID_ARGUMENT error.
   */
  selectedMemberships?: SelectedMemberships;
  /**
   * Whether to calculate shipping in the calculation request.
   *
   * Default: `true`
   * @internal
   */
  calculateShipping?: boolean | null;
  /** Buyer phone number. Used to get shipping rates. */
  buyerPhone?: string | null;
  /**
   * Whether to calculate discount rules in the calculation request. If not passed, discount rules are being calculated.
   * @internal
   */
  calculateDiscountRules?: boolean | null;
  /**
   * Shipping information that will be used instead of calculated shipping info.
   * @internal
   * @maxSize 1
   */
  shippingOptions?: CarrierShippingOption[];
  /**
   * Applied discounts that will be used instead of calculated discounts.
   * @internal
   * @maxSize 320
   */
  appliedDiscounts?: AppliedDiscount[];
  /**
   * Additional Fees that will be used instead of calculated additional fees
   * @internal
   * @maxSize 100
   */
  additionalFees?: AdditionalFee[];
  /**
   * List of data that should not be fetched by Totals calculator
   * @internal
   */
  skipDataFetch?: DataFetchTypeWithLiterals[];
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   */
  purchaseFlowId?: string | null;
  /**
   * Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.
   * For example, a Wix Pay Link ID.
   * @maxSize 20
   */
  externalReferences?: ExternalReference[];
  /**
   * The business location ID to be used as the basis for some calculations.
   * Learn more about the [Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).
   * @format GUID
   */
  businessLocationId?: string | null;
  /**
   * DEPRECATED: Use `giftCards.redeemAmount` instead.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   * @deprecated DEPRECATED: Use `giftCards.redeemAmount` instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardRedeemAmount?: string | null;
  /**
   * Buyer contact ID.
   * @internal
   * @format GUID
   */
  contactId?: string | null;
  /** [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields). */
  extendedFields?: ExtendedFields;
  /**
   * Exempt group to use for tax calculations.
   * @internal
   * @format GUID
   */
  taxExemptGroupId?: string | null;
  /**
   * Controls behavior when the selected shipping option is unavailable:
   * + `true` - An alternative shipping option is automatically selected from the available options.
   * + `false` - An alternative shipping option is not automatically selected from the available options,
   * and a calculation error will be returned to signal that the selected option is unavailable.
   *
   * Default: `true`
   * @internal
   */
  selectedShippingFallback?: boolean | null;
  /**
   * Whether SPECIFIC_ITEMS discounts are applied directly to item prices.
   *
   * When `true`:
   * + SPECIFIC_ITEMS discounts reduce item `price`, which is reflected in the subtotal.
   * + GLOBAL discounts are applied on top of the subtotal, without modifying item `price`.
   *
   * When `false` (current behavior):
   * + All coupons and merchant discounts are treated as GLOBAL; item prices are unchanged.
   *
   * Note: SPECIFIC_ITEMS automatic discounts are already applied to item prices, regardless of this setting.
   *
   * Default: `false`
   * @internal
   */
  applyItemDiscountsToPrices?: boolean;
  /**
   * Gift cards to apply.
   * @internal
   * @maxSize 5
   */
  giftCards?: GiftCardInput[];
  /**
   * Membership selection (existing + future) to be validated and enhanced.
   * When set, memberships are resolved via the EnhanceAndValidateSelection SPI flow.
   * Mutually exclusive with `selected_memberships` - providing both returns an INVALID_ARGUMENT error.
   * @internal
   */
  membershipSelection?: MembershipSelection;
  /**
   * Currency of the provided amounts. When set, the calculation is performed and rounded in this currency.
   *
   * Default: The site's default currency.
   * @internal
   * @format CURRENCY
   */
  amountCurrency?: string | null;
  /**
   * ID of the site payment method type the customer selected to pay with. See the [Site Payment Method Types API](https://dev.wix.com/docs/api-reference/business-management/payments/site-payment-method-types/introduction).
   * @internal
   * @format GUID
   */
  sitePaymentMethodTypeId?: string | null;
}

/** @oneof */
export interface CalculateTotalsRequestCouponOneOf {
  /** Coupon ID. */
  couponId?: string | null;
  /** Coupon code. */
  couponCode?: string | null;
}

/** @oneof */
export interface CalculateTotalsRequestGiftCardOneOf {
  /**
   * DEPRECATED: Use gift_cards instead.
   * @deprecated DEPRECATED: Use gift_cards instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardCode?: string | null;
}

export interface LineItem {
  /**
   * Line item ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Quantity.
   * @max 100000
   */
  quantity?: number | null;
  /**
   * Optional references to the line item's origin catalog.
   * See [Catalog SPI](https://bo.wix.com/wix-docs/rest/ecommerce/catalog-spi/introduction) for more details.
   * This field may be empty in the case of a custom line item.
   * currently we only use the catalog app id to set tax=0 for specific apps.
   */
  catalogReference?: CatalogReference;
  /**
   * Product/booking/event name.
   * @maxLength 200
   */
  productName?: string | null;
  /**
   * Catalog price.
   * @decimalValue options { gte:0 }
   */
  price?: string;
  /** Physical properties (if applicable). */
  physicalProperties?: PhysicalProperties;
  /**
   * Coupon scopes - which app and items a coupon applies to.
   * This field is internal to Wix, and should be used by Bookings, Stores and Events as used by the current [Coupons API](https://bo.wix.com/wix-docs/rest/stores/coupons/valid-scope-values).
   * @internal
   */
  couponScopes?: Scope[];
  /**
   * Tax group ID.
   * @internal
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Shipping group ID.
   * @internal
   * @format GUID
   */
  shippingGroupId?: string | null;
  /** Subscription settings. */
  subscriptionSettings?: SubscriptionSettings;
  /**
   * Type of selected payment option for current item.
   *
   * Default: `FULL_PAYMENT_ONLINE`
   */
  paymentOption?: PaymentOptionTypeWithLiterals;
  /** Service properties. When relevant, this contains information such as date and number of participants. */
  serviceProperties?: ServiceProperties;
  /**
   * In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.
   * + For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.
   * + In most cases, this field has the same value as `catalogReference.catalogItemId`.
   * + Used in membership validation.
   * @minLength 1
   * @maxLength 36
   */
  rootCatalogItemId?: string | null;
  /**
   * Optional - partial amount of item's price to be paid now for checkout cases with DEPOSIT_ONLINE payment option
   * If omitted - item's price will not be split and is expected to be paid in single installment
   * @format DECIMAL_VALUE
   */
  depositAmount?: string | null;
  /**
   * full price of the item before all the discounts
   * @format DECIMAL_VALUE
   */
  fullPrice?: string | null;
  /**
   * The Item's Delivery Profile Id
   * @format GUID
   */
  deliveryProfileId?: string | null;
  /** Item type. Either a preset type or custom. */
  itemType?: ItemType;
  /** Address to use for tax calculation purposes. */
  taxableAddress?: TaxableAddress;
  /**
   * Additional Fees to be added to used for calculation.
   * @readonly
   * @maxSize 5
   */
  additionalFees?: ApiV1AdditionalFee[];
  /**
   * Modifier groups that were added to the item.
   * @maxSize 25
   */
  modifierGroups?: ModifierGroup[];
  /**
   * Platform fee that should be charged for the item.
   * @internal
   */
  platformFee?: PlatformFee;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface PhysicalProperties {
  /** Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request. */
  weight?: number | null;
  /**
   * Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).
   * @maxLength 40
   */
  sku?: string | null;
  /** Whether this line item is shippable. */
  shippable?: boolean;
  /**
   * Product dimensions (length, width, height of the physical product).
   * @internal
   */
  productDimensions?: Dimensions;
  /**
   * Package dimensions (length, width, height of the shipping package).
   * @internal
   */
  packageDimensions?: Dimensions;
}

export interface Dimensions {
  /**
   * Length. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  length?: string | null;
  /**
   * Width. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  width?: string | null;
  /**
   * Height. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  height?: string | null;
  /** Unit of measurement for dimensions (length, width, height). */
  unit?: DimensionsUnitWithLiterals;
}

export enum DimensionsUnit {
  UNKNOWN_DIMENSIONS_UNIT = 'UNKNOWN_DIMENSIONS_UNIT',
  /** Millimeters. */
  MM = 'MM',
  /** Centimeters. */
  CM = 'CM',
  /** Meters. */
  M = 'M',
  /** Inches. */
  IN = 'IN',
  /** Feet. */
  FT = 'FT',
  /** Yards. */
  YD = 'YD',
}

/** @enumType */
export type DimensionsUnitWithLiterals =
  | DimensionsUnit
  | 'UNKNOWN_DIMENSIONS_UNIT'
  | 'MM'
  | 'CM'
  | 'M'
  | 'IN'
  | 'FT'
  | 'YD';

export interface Scope {
  /** Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans) */
  namespace?: string;
  /** Coupon scope's applied group (e.g., event or ticket in Wix Events) */
  group?: Group;
}

export interface Group {
  /** Coupon scope's group (e.g., product or collection in Wix Stores). See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  name?: string;
  /** Item ID (when the coupon scope is limited to just one item). */
  entityId?: string | null;
}

export interface SubscriptionSettings {
  /** Frequency of recurring payment. Every day/week/month/year. */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * *Optional** - Interval of recurring payment. Defaults to 1 if not provided.
   * @internal
   * @min 1
   * @max 3650
   */
  interval?: number | null;
  /**
   * Whether this subscription offers a free trial period.
   * @internal
   */
  freeTrial?: boolean;
}

/** Frequency unit of recurring payment */
export enum SubscriptionFrequency {
  UNDEFINED = 'UNDEFINED',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type SubscriptionFrequencyWithLiterals =
  | SubscriptionFrequency
  | 'UNDEFINED'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

/** Type of selected payment option for catalog item */
export enum PaymentOptionType {
  /** The entire payment for this item happens as part of the checkout. */
  FULL_PAYMENT_ONLINE = 'FULL_PAYMENT_ONLINE',
  /** The entire payment for this item happens after checkout. For example, when using cash, check, or other offline payment methods. */
  FULL_PAYMENT_OFFLINE = 'FULL_PAYMENT_OFFLINE',
  /** Payment for this item is done by charging a membership. Any remaining amount not covered by the membership, such as item modifiers, is paid online. */
  MEMBERSHIP = 'MEMBERSHIP',
  /** Partial payment to be paid upfront during checkout. The initial amount to be paid for each line item is specified in `depositAmount`. */
  DEPOSIT_ONLINE = 'DEPOSIT_ONLINE',
  /** Payment for this item can only be done by charging a membership and must be manually redeemed in the dashboard by the site admin. When selected, `price` is `0`. */
  MEMBERSHIP_OFFLINE = 'MEMBERSHIP_OFFLINE',
  /**
   * Item price is charged to online membership. Any remaining amount not covered by the membership, such as item modifiers, is paid offline.
   * @documentationMaturity preview
   */
  MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER = 'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
}

/** @enumType */
export type PaymentOptionTypeWithLiterals =
  | PaymentOptionType
  | 'FULL_PAYMENT_ONLINE'
  | 'FULL_PAYMENT_OFFLINE'
  | 'MEMBERSHIP'
  | 'DEPOSIT_ONLINE'
  | 'MEMBERSHIP_OFFLINE'
  | 'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER';

export interface ServiceProperties {
  /**
   * Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.
   * For example, the start time of a class.
   */
  scheduledDate?: Date | null;
  /**
   * The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.
   * @min 1
   * @max 10000
   */
  numberOfParticipants?: number | null;
  /**
   * Date and time the service is to be ended, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.
   * For example, the date and time a class will end.
   * @internal
   */
  endDate?: Date | null;
}

export interface ItemType extends ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: ItemTypePresetWithLiterals;
  /** Custom item type. When none of the preset types are suitable, specifies the custom type. */
  custom?: string;
}

/** @oneof */
export interface ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: ItemTypePresetWithLiterals;
  /** Custom item type. When none of the preset types are suitable, specifies the custom type. */
  custom?: string;
}

export enum ItemTypePreset {
  UNRECOGNISED = 'UNRECOGNISED',
  PHYSICAL = 'PHYSICAL',
  DIGITAL = 'DIGITAL',
  GIFT_CARD = 'GIFT_CARD',
  SERVICE = 'SERVICE',
}

/** @enumType */
export type ItemTypePresetWithLiterals =
  | ItemTypePreset
  | 'UNRECOGNISED'
  | 'PHYSICAL'
  | 'DIGITAL'
  | 'GIFT_CARD'
  | 'SERVICE';

export interface TaxableAddress extends TaxableAddressTaxableAddressDataOneOf {
  /** taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly. */
  addressType?: TaxableAddressTypeWithLiterals;
}

/** @oneof */
export interface TaxableAddressTaxableAddressDataOneOf {
  /** taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly. */
  addressType?: TaxableAddressTypeWithLiterals;
}

export enum TaxableAddressType {
  BUSINESS = 'BUSINESS',
  BILLING = 'BILLING',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type TaxableAddressTypeWithLiterals =
  | TaxableAddressType
  | 'BUSINESS'
  | 'BILLING'
  | 'SHIPPING';

export interface ApiV1AdditionalFee {
  /**
   * Additional fee's unique code or ID.
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Additional fee's name.
   *
   * Max: 50 characters
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Total additional fees. This `price` does not include any taxes that may apply to these additional fees.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:2 }
   */
  price?: string;
  /**
   * Tax details.
   *
   * > **Note:** Tax is not calculated in the returned `price` even when `taxDetails.taxable` is `true`.
   */
  taxDetails?: V1TaxDetails;
  /**
   * Number of subscription cycle this fee applies to
   * default None - all billing cycle
   * @internal
   * @min 1
   * @max 999
   */
  subscriptionCycles?: number | null;
}

/** Tax details. */
export interface V1TaxDetails {
  /** Whether additional fee is taxable. */
  taxable?: boolean;
  /**
   * Reserved for internal use.
   * @format GUID
   */
  taxGroupId?: string | null;
}

export interface ModifierGroup {
  /**
   * Modifier group ID.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string;
  /** Modifier group name. */
  name?: TranslatableString;
  /**
   * List of modifiers in this group.
   * @minSize 1
   * @maxSize 25
   */
  modifiers?: ItemModifier[];
}

export interface TranslatableString {
  /**
   * __Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   *
   * Min: 1 character.
   * Max: 200 characters.
   * @minLength 1
   * @maxLength 200
   */
  original?: string;
  /**
   * String translated into the buyer's language.
   *
   * Min: 1 character.
   * Max: 400 characters.
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 400
   */
  translated?: string | null;
}

export interface ItemModifier {
  /**
   * Modifier ID.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string;
  /**
   * The quantity of this modifier.
   * @min 1
   * @max 100000
   */
  quantity?: number | null;
  /** Primary display label for the modifier. */
  label?: TranslatableString;
  /** Additional details. */
  details?: TranslatableString;
  /**
   * The price of the modifier.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   */
  price?: string | null;
}

export interface PlatformFee {
  /** Platform fee name. */
  name?: TranslatableString;
  /** Platform fee charge type. */
  chargeType?: PlatformFeeChargeTypeWithLiterals;
  /**
   * Percentage rate charged as platform fee.
   *
   * The fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:1, maxScale:4 }
   */
  percentageRate?: string;
}

export enum PlatformFeeChargeType {
  /** The platform fee is passed on to the customer. */
  PASS_ON = 'PASS_ON',
  /** The platform fee is absorbed by the merchant. */
  ABSORBED = 'ABSORBED',
}

/** @enumType */
export type PlatformFeeChargeTypeWithLiterals =
  | PlatformFeeChargeType
  | 'PASS_ON'
  | 'ABSORBED';

/** Physical address */
export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 50
   */
  postalCode?: string | null;
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Main address line (usually street name and number).
   * @maxLength 150
   */
  addressLine1?: string | null;
  /**
   * Free text providing more detailed address info. Usually contains apt, suite, floor.
   * @maxLength 100
   */
  addressLine2?: string | null;
  /** Geocode object containing latitude and longitude coordinates. */
  location?: AddressLocation;
  /**
   * Country's full name.
   * @readonly
   */
  countryFullname?: string | null;
  /**
   * Subdivision full-name.
   * @readonly
   */
  subdivisionFullname?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
  /**
   * Optional address line 1
   * @internal
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface SelectedShippingOption {
  /**
   * Carrier ID.
   * @format GUID
   */
  carrierId?: string | null;
  /**
   * Selected shipping option code. For example, "usps_std_overnight".
   * @minLength 1
   * @maxLength 100
   */
  code?: string;
}

export interface SelectedMemberships {
  /**
   * Selected memberships.
   * @maxSize 300
   */
  memberships?: SelectedMembership[];
}

export interface SelectedMembership {
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 200
   */
  _id?: string;
  /**
   * ID of the app providing this payment option.
   * @format GUID
   */
  appId?: string;
  /**
   * IDs of the line items this membership applies to.
   * @minSize 1
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface CarrierShippingOption {
  /**
   * This carrier's unique ID
   * @format GUID
   */
  carrierId?: string | null;
  /** Shipping service options offered by this carrier for this request */
  shippingOptions?: ShippingOption;
}

export interface ShippingOption {
  /**
   * Unique code of provided shipping option like "usps_std_overnight".
   * For legacy calculators this would be the UUID of the option.
   * @maxLength 100
   */
  code?: string;
  /**
   * Title of the option, such as USPS Standard Overnight Delivery (in the requested locale).
   * For example, "Standard" or "First-Class Package International".
   * @maxLength 250
   */
  title?: string;
  /** Delivery logistics. */
  logistics?: DeliveryLogistics;
  /** Sipping price information. */
  cost?: ShippingPrice;
  /**
   * Delivery solution allocations to different delivery carriers and delivery regions
   * @maxSize 300
   */
  deliveryAllocations?: DeliveryAllocation[];
  /** If the delivery solution is a partial and doesn't apply to all items. */
  partial?: boolean | null;
}

export interface DeliveryLogistics {
  /**
   * Expected delivery time, in free text. For example, "3-5 business days".
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /**
   * Instructions for caller, e.g for pickup: "Please deliver during opening hours, and please don't park in disabled parking spot".
   * @maxLength 1000
   */
  instructions?: string | null;
  /** Pickup details. */
  pickupDetails?: PickupDetails;
  /**
   * Expected delivery time slot (from and to time stamps representation)
   * @internal
   */
  deliveryTimeSlot?: DeliveryTimeSlot;
}

export interface PickupDetails {
  /** Pickup address. */
  address?: Address;
  /**
   * Whether the pickup address is that of a business - this may effect tax calculation.
   * @deprecated
   */
  businessLocation?: boolean;
  /** Pickup method */
  pickupMethod?: PickupMethodWithLiterals;
}

export enum PickupMethod {
  STORE_PICKUP = 'STORE_PICKUP',
  PICKUP_POINT = 'PICKUP_POINT',
}

/** @enumType */
export type PickupMethodWithLiterals =
  | PickupMethod
  | 'STORE_PICKUP'
  | 'PICKUP_POINT';

export interface DeliveryTimeSlot {
  /** starting time of the delivery time slot */
  from?: Date | null;
  /** ending time of the delivery time slot */
  to?: Date | null;
}

export interface ShippingPrice {
  /** Shipping price. */
  price?: MultiCurrencyPrice;
  /** Other costs such as insurance, handling & packaging for fragile items, etc. */
  otherCharges?: OtherCharge[];
}

export interface MultiCurrencyPrice {
  /**
   * Amount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  amount?: string;
  /**
   * Converted amount.
   * @readonly
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  convertedAmount?: string;
  /**
   * Amount formatted with currency symbol.
   * @readonly
   */
  formattedAmount?: string;
  /**
   * Converted amount formatted with currency symbol.
   * @readonly
   */
  formattedConvertedAmount?: string;
}

export interface OtherCharge {
  /** Type of additional cost. */
  type?: ChargeTypeWithLiterals;
  /** Price of added cost. */
  price?: MultiCurrencyPrice;
  /**
   * Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.
   * @maxLength 250
   */
  details?: string | null;
}

export enum ChargeType {
  HANDLING_FEE = 'HANDLING_FEE',
  INSURANCE = 'INSURANCE',
}

/** @enumType */
export type ChargeTypeWithLiterals = ChargeType | 'HANDLING_FEE' | 'INSURANCE';

export interface DeliveryAllocation {
  /** The delivery option's carrier details, could be multiple if the delivery option is a combination of multiple carriers */
  deliveryCarrier?: Carrier;
  /** The delivery region that are relevant for this delivery solution. */
  deliveryRegion?: Region;
  /** Populated if the delivery solution is a partially supplied by this carrier. */
  applicableLineItems?: ApplicableLineItems;
}

export interface Carrier {
  /**
   * The carrier app id
   * @format GUID
   */
  appId?: string | null;
  /**
   * Unique code that acts as an ID for a shipping rate. For example, `"usps_std_overnight"`.
   * @maxLength 250
   */
  code?: string;
}

export interface Region {
  /**
   * The delivery region id.
   * @format GUID
   */
  _id?: string | null;
  /**
   * The delivery region name.
   * @maxLength 100
   */
  name?: string | null;
}

export interface ApplicableLineItems {
  /**
   * Line items that the delivery solution is for.
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface AppliedDiscount extends AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
  /** Discount type. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * IDs of line items the discount applies to.
   * @format GUID
   */
  lineItemIds?: string[];
  /**
   * Discount ID.
   * @internal
   * @format GUID
   */
  _id?: string | null;
  /**
   * Discount distribution.
   * Each entry contains the discount amount allocated to a specific line item.
   * Returned only when `apply_item_discounts_to_prices` is enabled.
   * @internal
   * @maxSize 300
   * @readonly
   */
  lineItemDiscounts?: LineItemDiscount[];
  /**
   * Number of subscription cycle this discount applies to
   * default None - all billing cycle
   * @internal
   * @min 1
   * @max 999
   */
  subscriptionCycles?: number | null;
  /**
   * A list of item combinations for this applied discount.
   * Each entry represents a unique combination of line items that triggered
   * or received this discount, along with how many times that combination was applied together.
   * Relevant ONLY for BXGY and Quantity-based promotions.
   * In BXGY the combination will contain the "X" items with discount amount of 0.
   * @internal
   * @maxSize 1000
   */
  itemCombinations?: ItemCombination[];
}

/** @oneof */
export interface AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
}

export enum DiscountType {
  GLOBAL = 'GLOBAL',
  SPECIFIC_ITEMS = 'SPECIFIC_ITEMS',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'GLOBAL'
  | 'SPECIFIC_ITEMS'
  | 'SHIPPING';

/** Coupon */
export interface Coupon {
  /** Coupon ID. */
  _id?: string;
  /** Coupon code. */
  code?: string;
  /** Coupon value. */
  amount?: MultiCurrencyPrice;
  /** Coupon name. */
  name?: string;
  /**
   * Coupon type: We want it to be an enum and not a string but currently we have no time to do it so we leave it as is to be aligned with cart summary.
   * @internal
   * @deprecated
   */
  couponType?: string;
}

export interface MerchantDiscount {
  /** Discount value. */
  amount?: MultiCurrencyPrice;
  /**
   * Discount Percentage. Will be calculated from items price before other discounts.
   * @min 0.01
   * @max 100
   */
  percentage?: number | null;
}

export interface DiscountRule {
  /**
   * Discount rule ID
   * @format GUID
   */
  _id?: string;
  /** Discount rule name */
  name?: DiscountRuleName;
  /** Discount value. */
  amount?: MultiCurrencyPrice;
}

export interface DiscountRuleName {
  /**
   * Original discount rule name (in site's default language).
   * @minLength 1
   * @maxLength 256
   */
  original?: string;
  /**
   * Translated discount rule name according to buyer language. Defaults to `original` when not provided.
   * @minLength 1
   * @maxLength 500
   */
  translated?: string | null;
}

export interface LineItemDiscount {
  /**
   * ID of line item the discount applies to.
   * @format GUID
   */
  _id?: string;
  /** Discount value. */
  totalDiscountAmount?: MultiCurrencyPrice;
}

export interface ItemCombination {
  /**
   * The number of times this exact combination of items (with the specified quantities) was applied together in the order.
   * @min 1
   * @max 100000
   */
  count?: number;
  /**
   * Line items that participated together in this combination.
   * @minSize 1
   * @maxSize 100
   */
  lineItems?: ItemCombinationLineItem[];
}

export interface ItemCombinationLineItem {
  /**
   * The unique ID of the line item to which this discount applies.
   * @format GUID
   */
  lineItemId?: string;
  /** Total discount amount for all units (quantity) of this line item in this combination. */
  discountAmount?: MultiCurrencyPrice;
  /**
   * Number of units from this line item that participated in a single combination.
   * @min 1
   * @max 100000
   */
  quantity?: number;
}

export interface AdditionalFee {
  /**
   * Additional fee's unique code (or ID) for future processing
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Name of the additional fee. For example, "Gift wrapping".
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Additional fee's price
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  price?: string;
  /**
   * Currency
   * @format CURRENCY
   */
  currency?: string;
  /** Tax details */
  taxDetails?: TaxDetails;
  /**
   * Provider's app id
   * @minLength 1
   * @maxLength 100
   */
  providerAppId?: string | null;
  /**
   * Optional - Line items associated with this additional fee.
   * If no `lineItemIds` are provided, the fee will be associated with the whole cart/checkout/order.
   * @format GUID
   */
  lineItemIds?: string[];
  /**
   * Number of subscription cycle this fee applies to
   * default None - all billing cycle
   * @internal
   * @min 1
   * @max 999
   */
  subscriptionCycles?: number | null;
  /**
   * The translated name of the additional fee. The translation language is determined by the `languages` field in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @minLength 1
   * @maxLength 50
   */
  translatedName?: string | null;
}

/** Tax Details. */
export interface TaxDetails {
  /** Indication if additional fee is taxable or not */
  taxable?: boolean;
  /**
   * Optional tax group ID to use when calculating tax for this additional fee
   * @format GUID
   */
  taxGroupId?: string | null;
  /** Address type to calculate tax against for this fee. */
  taxableAddress?: TaxableAddress;
}

export enum DataFetchType {
  SHIPPING = 'SHIPPING',
  DISCOUNT = 'DISCOUNT',
  ADDITIONAL_FEES = 'ADDITIONAL_FEES',
  ORDER_VALIDATION = 'ORDER_VALIDATION',
}

/** @enumType */
export type DataFetchTypeWithLiterals =
  | DataFetchType
  | 'SHIPPING'
  | 'DISCOUNT'
  | 'ADDITIONAL_FEES'
  | 'ORDER_VALIDATION';

export interface ExternalReference {
  /**
   * ID of the app associated with the purchase flow.
   * For example, the Wix Pay Links app ID.
   * @format GUID
   * @immutable
   */
  appId?: string;
  /**
   * Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.
   * For example, a Wix Pay Link ID.
   * @minLength 1
   * @maxLength 100
   * @immutable
   */
  resourceId?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GiftCardInput {
  /**
   * Gift card code.
   * @minLength 8
   * @maxLength 20
   */
  code?: string;
  /**
   * Amount to redeem from this gift card. When not provided, the full available balance is redeemed up to the order total.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   */
  redeemAmount?: string | null;
}

/**
 * Buyer's membership selection (existing + future) to validate and enhance
 * via the EnhanceAndValidateSelection SPI flow.
 */
export interface MembershipSelection {
  /**
   * Existing memberships the buyer selected to redeem.
   * @maxSize 300
   */
  existingMemberships?: ExistingMembershipSelection[];
  /**
   * Future memberships the buyer intends to purchase.
   * @maxSize 300
   */
  futureMemberships?: FutureMembershipSelection[];
}

export interface ExistingMembershipSelection {
  /**
   * ID of the app providing this membership.
   * @format GUID
   */
  appId?: string;
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 100
   */
  membershipId?: string;
  /**
   * IDs of the request line items to redeem against this membership.
   * @minSize 1
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface FutureMembershipSelection {
  /** Catalog reference of the plan to be purchased. */
  catalogReference?: CatalogReference;
  /**
   * ID of the benefit the line items will be redeemed against.
   * @maxLength 200
   */
  benefitId?: string;
  /**
   * IDs of the request line items to cover with this future membership.
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface CalculateTotalsResponse {
  /** Calculate line items. */
  calculatedLineItems?: CalculatedLineItem[];
  /** Price summary. */
  priceSummary?: PriceSummary;
  /**
   * DEPRECATED: Use gift_cards instead.
   * @deprecated DEPRECATED: Use gift_cards instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCard?: GiftCard;
  /** Tax summary. */
  taxSummary?: TaxSummary;
  /** Shipping information. */
  shippingInfo?: ShippingInformation;
  /**
   * Applied discounts.
   * @maxSize 320
   */
  appliedDiscounts?: AppliedDiscount[];
  /** Calculation errors. */
  calculationErrors?: CalculationErrors;
  /** Weight unit. */
  weightUnit?: WeightUnitWithLiterals;
  /**
   * The currency the price calculations were performed in.
   * This is the business’s default currency: the base currency configured for the site, which remains constant regardless of the [customer’s currency selection](https://support.wix.com/en/article/multicurrency-an-overview).
   * When `amountCurrency` is specified on the request, this returns that value instead.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * The currency the converted price calculations are in.
   * This is the currency [selected by the customer](https://support.wix.com/en/article/multicurrency-an-overview), which may differ from `currency`.
   * When `amountCurrency` is specified on the request, nothing is converted and this returns that value.
   * @readonly
   * @format CURRENCY
   */
  conversionCurrency?: string;
  /** Whether tax is included in price. */
  taxIncludedInPrice?: boolean;
  /**
   * Minimal amount to pay in order to place the order.
   * @readonly
   */
  payNow?: PriceSummary;
  /**
   * Remaining amount for the order to be fully paid.
   * @readonly
   */
  payLater?: PriceSummary;
  /** Information about valid and invalid memberships, and which ones are selected for usage. */
  membershipOptions?: MembershipOptions;
  /**
   * Additional Fees
   * @maxSize 100
   */
  additionalFees?: V1AdditionalFee[];
  /**
   * The rate used when converting from the site currency to the checkout currency.
   * @readonly
   * @decimalValue options { gt:0, lte:1000000000000000 }
   */
  checkoutConversionRate?: string | null;
  /**
   * The pay now total amount after all gift cards are applied.
   * @readonly
   */
  payNowTotalAfterGiftCard?: MultiCurrencyPrice;
  /**
   * The total amount after all gift cards are applied.
   * @readonly
   */
  totalAfterGiftCard?: MultiCurrencyPrice;
  /**
   * Payment for subscriptions after free trial period.
   * @readonly
   */
  payAfterFreeTrial?: PriceSummary;
  /**
   * Platform fee summery.
   * @internal
   */
  platformFeeSummary?: PlatformFeeSummary;
  /**
   * Details of applied gift cards.
   * @internal
   */
  giftCards?: GiftCard[];
}

export interface CalculatedLineItem {
  /** Line item ID. */
  lineItemId?: string;
  /** Price breakdown for this line item. */
  pricesBreakdown?: LineItemPricesData;
  /**
   * Type of selected payment option for current item. Supported values:
   * + `"FULL_PAYMENT_ONLINE"` - The entire payment for this item will happen as part of the checkout
   * + `"FULL_PAYMENT_OFFLINE"` - The entire payment for this item will happen after the checkout
   * + `"MEMBERSHIP"` - This item cannot be paid via monetary payment options, only via non monetary option such membership. When this option is used, price must be set to 0
   * + `"DEPOSIT_ONLINE"` -  Partial payment of the given item will happen as part of the checkout. Amount to be paid is defined by deposit_amount field.
   */
  paymentOption?: PaymentOptionTypeWithLiterals;
  /** tax calculation address. */
  taxableAddress?: TaxableAddress;
  /**
   * Calculated modifiers.
   * @internal
   * @maxSize 625
   */
  modifiers?: CalculatedItemModifier[];
}

export interface LineItemPricesData {
  /** Total price after discounts, tax, and modifiers. */
  totalPriceAfterTax?: MultiCurrencyPrice;
  /** Total price after discounts and modifiers, and before tax. */
  totalPriceBeforeTax?: MultiCurrencyPrice;
  /** Tax details. */
  taxDetails?: ItemTaxFullDetails;
  /** Total discount applied for the line item. */
  totalDiscount?: MultiCurrencyPrice;
  /** Catalog price after catalog-defined discount, automatic discounts, and modifiers. */
  price?: MultiCurrencyPrice;
  /**
   * Item price before automatic discounts, coupons, and global discounts; after catalog-defined discounts and modifiers.
   * Defaults to `price` when not provided.
   */
  priceBeforeDiscounts?: MultiCurrencyPrice;
  /** Total line item price after catalog-defined discounts, automatic discounts, and modifiers. */
  lineItemPrice?: MultiCurrencyPrice;
  /** Catalog price before any discounts, with modifiers. */
  fullPrice?: MultiCurrencyPrice;
  /** Sum of all modifiers for a single unit of the item. */
  modifiers?: MultiCurrencyPrice;
  /**
   * Item price before tax, automatic discounts, coupons, and global discounts; after catalog-defined discounts and modifiers.
   * @internal
   */
  priceBeforeDiscountsAndTax?: MultiCurrencyPrice;
  /**
   * Partial payment out of `price` to be paid upfront during the checkout.
   * Applies to catalog items with `lineItem.paymentOption` type `DEPOSIT_ONLINE` only.
   */
  depositAmount?: MultiCurrencyPrice;
}

export interface ItemTaxFullDetails {
  /** Amount for which tax is calculated. */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * Tax group ID, if specified.
   * @internal
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax rate %, as a decimal point between 0 and 1.
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   */
  taxRate?: string;
  /** Calculated tax, based on `taxable_amount` and `tax_rate`. */
  totalTax?: MultiCurrencyPrice;
  /**
   * If breakdown exists, the sum of rates in the breakdown must equal `tax_rate`. Deprecated - use 'tax_breakdown' instead.
   * @readonly
   * @deprecated
   */
  rateBreakdown?: TaxRateBreakdown[];
  /**
   * The amount of this line item that was exempt.
   * @internal
   */
  exemptAmount?: MultiCurrencyPrice;
  /**
   * True for items we have a tax applied on them
   * @internal
   */
  isItemTaxable?: boolean | null;
  /**
   * True if the lineAmount include tax in it.
   * @internal
   */
  isTaxIncluded?: boolean | null;
  /**
   * The calculator that calculated this line tax
   * @internal
   * @maxLength 25
   */
  calculatorName?: string | null;
  /**
   * tax information for a line item.
   * @maxSize 1000
   * @readonly
   */
  taxBreakdown?: TaxBreakdown[];
}

export interface TaxRateBreakdown {
  /**
   * Name of tax against which the calculation was performed.
   * @maxLength 100
   */
  name?: string;
  /**
   * Rate at which this tax detail was calculated.
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   */
  rate?: string;
  /** Amount of tax for this tax detail. */
  tax?: MultiCurrencyPrice;
  /**
   * The type of tax that was calculated
   * @internal
   */
  taxType?: string | null;
  /**
   * The name of the jurisdiction in which this tax detail applies
   * @internal
   */
  jurisdiction?: string | null;
  /**
   * The type of the jurisdiction in which this tax detail applies(Country,State,County,City,Special).
   * @internal
   */
  jurisdictionType?: string | null;
  /**
   * The amount of this line item that was exempt from this authority.
   * @internal
   */
  exemptAmount?: MultiCurrencyPrice;
  /**
   * ids of the used exemptions from the TaxEstimate exemptions array.
   * @internal
   * @maxSize 1000
   */
  exemptionIds?: number[];
  /**
   * The taxable amount of this tax detail
   * @internal
   */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * The Taxes/Fee component. True if the fee is applied.
   * @internal
   */
  isFee?: boolean | null;
}

/**
 * TaxBreakdown represents tax information for a line item.
 * It holds the tax amount and the tax rate for each tax authority that apply on the line item.
 */
export interface TaxBreakdown {
  /**
   * The name of the jurisdiction to which this tax detail applies. For example, "New York" or "Quebec".
   * @maxLength 200
   */
  jurisdiction?: string | null;
  /** The amount of this line item price that was considered nontaxable. (Decimal value) */
  nonTaxableAmount?: MultiCurrencyPrice;
  /**
   * The rate at which this tax detail was calculated, e.g 0.1000 signifies 10% tax and 2.0000 signifies 200% tax. (Decimal value)
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string | null;
  /** The amount of tax estimated for this line item. (Decimal value) */
  taxAmount?: MultiCurrencyPrice;
  /** The taxable amount of this line item. */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * The type of tax that was calculated. Depends on the jurisdiction's tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.
   * @maxLength 200
   */
  taxType?: string | null;
  /**
   * The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.
   * This name should be explicit enough to allow the merchant to understand what tax was calculated.
   * @maxLength 200
   */
  taxName?: string | null;
  /** The type of the jurisdiction in which this tax detail applies. */
  jurisdictionType?: JurisdictionTypeWithLiterals;
  /**
   * The amount of this line item that was exempt from this authority.
   * @internal
   */
  exemptAmount?: MultiCurrencyPrice;
}

/** JurisdictionType represents the type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special). */
export enum JurisdictionType {
  UNDEFINED = 'UNDEFINED',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  SPECIAL = 'SPECIAL',
}

/** @enumType */
export type JurisdictionTypeWithLiterals =
  | JurisdictionType
  | 'UNDEFINED'
  | 'COUNTRY'
  | 'STATE'
  | 'COUNTY'
  | 'CITY'
  | 'SPECIAL';

export interface CalculatedItemModifier {
  /**
   * Modifier ID.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string;
  /**
   * ID of the group this modifier belongs to.
   * @minLength 1
   * @maxLength 36
   */
  groupId?: string;
  /**
   * Modifier price.
   * @readonly
   */
  price?: MultiCurrencyPrice;
}

export interface PriceSummary {
  /** Subtotal of all line items, before discounts and before tax. */
  subtotal?: MultiCurrencyPrice;
  /** Total shipping price, before discounts and before tax. */
  shipping?: MultiCurrencyPrice;
  /** Total tax. */
  tax?: MultiCurrencyPrice;
  /** Total calculated discount value. */
  discount?: MultiCurrencyPrice;
  /** Total price after discounts, gift cards, and tax. */
  total?: MultiCurrencyPrice;
  /** Total additional fees price before tax. */
  additionalFees?: MultiCurrencyPrice;
}

export interface GiftCard {
  /**
   * Gift Card ID.
   * @deprecated
   */
  _id?: string;
  /** Gift card obfuscated code. */
  obfuscatedCode?: string;
  /** Actual amount to be redeemed from the gift card. */
  amount?: MultiCurrencyPrice;
  /**
   * App ID of the gift card provider.
   * @format GUID
   */
  appId?: string;
  /**
   * External ID in the gift card provider's system.
   * Used for integration and tracking across different platforms.
   * @minLength 1
   * @maxLength 50
   */
  externalId?: string | null;
  /** Requested amount to redeem from the gift card. */
  requestedAmount?: MultiCurrencyPrice;
  /**
   * Full gift card code. Echoed from the request input for correlation.
   * @internal
   * @minLength 8
   * @maxLength 20
   */
  code?: string;
}

export interface TaxSummary {
  /**
   * Amount for which tax is calculated, added from line items.
   * @readonly
   */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * Calculated tax, added from line items.
   * @readonly
   */
  totalTax?: MultiCurrencyPrice;
  /**
   * Manual tax rate
   * @internal
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   * @readonly
   * @deprecated
   */
  manualTaxRate?: string;
  /**
   * Tax calculator that was active when the order was created.
   * @deprecated
   */
  calculationDetails?: TaxCalculationDetails;
  /**
   * Tax estimation id in tax service
   * @internal
   * @readonly
   */
  taxEstimationId?: string | null;
  /**
   * Average Tax Rate
   * @internal
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   * @readonly
   */
  averageTaxRate?: string | null;
  /**
   * The amount of this estimate that was exempt (for all line items).
   * @internal
   * @readonly
   */
  totalExempt?: MultiCurrencyPrice;
  /**
   * The summary of the tax breakdown for all the line items. It will hold for each tax name, the aggregated tax amount paid for it and the tax rate.
   * @internal
   * @readonly
   * @maxSize 50
   */
  aggregatedTaxBreakdown?: AggregatedTaxBreakdown[];
  /**
   * Default name of the tax that was calculated.
   * @internal
   * @maxLength 200
   */
  taxName?: string | null;
}

export interface TaxCalculationDetails
  extends TaxCalculationDetailsCalculationDetailsOneOf {
  /** Reason the manual calculation was used. */
  manualRateReason?: ManualCalculationReasonWithLiterals;
  /** Details of the fallback rate calculation. */
  autoTaxFallbackDetails?: AutoTaxFallbackCalculationDetails;
  /** Rate calculation type. */
  rateType?: RateTypeWithLiterals;
}

/** @oneof */
export interface TaxCalculationDetailsCalculationDetailsOneOf {
  /** Reason the manual calculation was used. */
  manualRateReason?: ManualCalculationReasonWithLiterals;
  /** Details of the fallback rate calculation. */
  autoTaxFallbackDetails?: AutoTaxFallbackCalculationDetails;
}

export enum RateType {
  /** no tax being collected for this request due to location of purchase */
  NO_TAX_COLLECTED = 'NO_TAX_COLLECTED',
  /** manual rate used for calculation */
  MANUAL_RATE = 'MANUAL_RATE',
  /** autotax rate used for calculation */
  AUTO_RATE = 'AUTO_RATE',
  /** fallback rate used for calculation */
  FALLBACK_RATE = 'FALLBACK_RATE',
}

/** @enumType */
export type RateTypeWithLiterals =
  | RateType
  | 'NO_TAX_COLLECTED'
  | 'MANUAL_RATE'
  | 'AUTO_RATE'
  | 'FALLBACK_RATE';

export enum ManualCalculationReason {
  /** user set calculator in Business Manager to be Manual */
  GLOBAL_SETTING_TO_MANUAL = 'GLOBAL_SETTING_TO_MANUAL',
  /** specific region is on manual even though Global setting is Auto-tax */
  REGION_SETTING_TO_MANUAL = 'REGION_SETTING_TO_MANUAL',
}

/** @enumType */
export type ManualCalculationReasonWithLiterals =
  | ManualCalculationReason
  | 'GLOBAL_SETTING_TO_MANUAL'
  | 'REGION_SETTING_TO_MANUAL';

export interface AutoTaxFallbackCalculationDetails {
  /** reason for fallback */
  fallbackReason?: FallbackReasonWithLiterals;
  /** invalid request (i.e. address), timeout, internal error, license error, and others will be encoded here */
  error?: ApplicationError;
}

export enum FallbackReason {
  /** auto-tax failed to be calculated */
  AUTO_TAX_FAILED = 'AUTO_TAX_FAILED',
  /** auto-tax was temporarily deactivated on a system-level */
  AUTO_TAX_DEACTIVATED = 'AUTO_TAX_DEACTIVATED',
}

/** @enumType */
export type FallbackReasonWithLiterals =
  | FallbackReason
  | 'AUTO_TAX_FAILED'
  | 'AUTO_TAX_DEACTIVATED';

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

/**
 * The summary of the tax breakdown for all the line items. It will hold for each tax name, the aggregated tax amount paid for it and the tax rate.
 * Tax breakdown is the tax amount split to the tax authorities that applied on the line item.
 */
export interface AggregatedTaxBreakdown {
  /**
   * The name of the tax against which this tax amount was calculated.
   * @maxLength 200
   */
  taxName?: string;
  /**
   * The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws.
   * @maxLength 200
   */
  taxType?: string;
  /**
   * The name of the jurisdiction in which this tax detail applies.
   * @maxLength 200
   */
  jurisdiction?: string;
  /** The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special). */
  jurisdictionTypeEnum?: JurisdictionTypeWithLiterals;
  /**
   * The rate at which this tax detail was calculated, e.g 0.1000 signifies 10% tax and 2.000 signifies 200% tax. (Decimal value)
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string;
  /** The sum of all the tax from line items that calculated by the tax identifiers. */
  aggregatedTaxAmount?: MultiCurrencyPrice;
  /** The sum of all the taxable amount from line items for tax identifiers. */
  aggregatedTaxableAmount?: MultiCurrencyPrice;
  /**
   * The aggregated exempt amount from all line items for tax identifiers.
   * @internal
   */
  aggregatedExemptAmount?: MultiCurrencyPrice;
}

export interface ShippingInformation {
  /** Shipping region. */
  region?: ShippingRegion;
  /** Selected shipping option. */
  selectedCarrierServiceOption?: SelectedCarrierServiceOption;
  /** All shipping options. */
  carrierServiceOptions?: CarrierServiceOption[];
}

export interface ShippingRegion {
  /**
   * Shipping region ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Shipping region name.
   * @maxLength 100
   */
  name?: string;
}

export interface SelectedCarrierServiceOption {
  /**
   * Unique identifier of selected option. For example, "usps_std_overnight".
   * @maxLength 100
   */
  code?: string;
  /**
   * Title of the option, such as USPS Standard Overnight Delivery (in the requested locale).
   * For example, "Standard" or "First-Class Package International".
   * @maxLength 250
   * @readonly
   */
  title?: string;
  /**
   * Delivery logistics.
   * @readonly
   */
  logistics?: DeliveryLogistics;
  /**
   * Shipping costs.
   * @readonly
   */
  cost?: SelectedCarrierServiceOptionPrices;
  /**
   * Were we able to find the requested shipping option, or otherwise we fallback to the default one (the first)
   * @readonly
   */
  requestedShippingOption?: boolean;
  /**
   * Other charges
   * @deprecated Other charges
   * @replacedBy additional_fees
   * @targetRemovalDate 2025-10-01
   */
  otherCharges?: SelectedCarrierServiceOptionOtherCharge[];
  /**
   * This carrier's unique ID
   * @format GUID
   */
  carrierId?: string | null;
  /**
   * Delivery solution allocations to different delivery carriers and delivery regions
   * @maxSize 300
   */
  deliveryAllocations?: DeliveryAllocation[];
  /** If the delivery solution is a partial and doesn't apply to all items. */
  partial?: boolean | null;
}

export interface SelectedCarrierServiceOptionPrices {
  /** Total shipping price, after discount and after tax. */
  totalPriceAfterTax?: MultiCurrencyPrice;
  /** Total price of shipping after discounts (when relevant), and before tax. */
  totalPriceBeforeTax?: MultiCurrencyPrice;
  /** Tax details. */
  taxDetails?: ItemTaxFullDetails;
  /** Shipping discount before tax. */
  totalDiscount?: MultiCurrencyPrice;
  /** Shipping price before discount and before tax. */
  price?: MultiCurrencyPrice;
}

export interface SelectedCarrierServiceOptionOtherCharge {
  /** Type of additional cost. */
  type?: ChargeTypeWithLiterals;
  /**
   * Details of the charge, such as 'Full Coverage Insurance of up to 80% of value of shipment'.
   * @maxLength 200
   */
  details?: string | null;
  /** Price of added charge. */
  cost?: SelectedCarrierServiceOptionPrices;
}

export interface CarrierServiceOption {
  /**
   * Carrier ID.
   * @format GUID
   */
  carrierId?: string;
  /** Shipping options offered by this carrier for this request. */
  shippingOptions?: ShippingOption[];
}

export interface CalculationErrors
  extends CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Errors related to the delivery carrier. */
  carrierErrors?: CarrierErrors;
  /** Error related to the selected shipping option. */
  selectedShippingOptionError?: Details;
  /** Tax calculation error. */
  taxCalculationError?: Details;
  /** Coupon calculation error. */
  couponCalculationError?: Details;
  /**
   * DEPRECATED: Use gift_card_calculation_errors instead.
   * @deprecated DEPRECATED: Use gift_card_calculation_errors instead.
   * @replacedBy gift_card_calculation_errors
   * @targetRemovalDate 2026-12-01
   */
  giftCardCalculationError?: Details;
  /** Order validation errors. */
  orderValidationErrors?: ApplicationError[];
  /**
   * Membership payment methods calculation errors
   * For example, will indicate that a line item that must be paid with membership payment doesn't have one or selected memberships are invalid
   */
  membershipError?: Details;
  /** Discount Rule calculation error. */
  discountsCalculationError?: Details;
  /**
   * Gift card calculation errors, one per failed card.
   * @internal
   */
  giftCardCalculationErrors?: GiftCardCalculationError[];
}

/** @oneof */
export interface CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Errors related to the delivery carrier. */
  carrierErrors?: CarrierErrors;
  /** Error related to the selected shipping option. */
  selectedShippingOptionError?: Details;
}

export interface Details extends DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
  /**
   * deprecated in API's - to enable migration from rendering arbitrary tracing to rest response
   * @deprecated
   */
  tracing?: Record<string, string>;
}

/** @oneof */
export interface DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
}

/**
 * example result:
 * {
 * "fieldViolations": [
 * {
 * "field": "fieldA",
 * "description": "invalid music note. supported notes: [do,re,mi,fa,sol,la,ti]",
 * "violatedRule": "OTHER",
 * "ruleName": "INVALID_NOTE",
 * "data": {
 * "value": "FI"
 * }
 * },
 * {
 * "field": "fieldB",
 * "description": "field value out of range. supported range: [0-20]",
 * "violatedRule": "MAX",
 * "data": {
 * "threshold": 20
 * }
 * },
 * {
 * "field": "fieldC",
 * "description": "invalid phone number. provide a valid phone number of size: [7-12], supported characters: [0-9, +, -, (, )]",
 * "violatedRule": "FORMAT",
 * "data": {
 * "type": "PHONE"
 * }
 * }
 * ]
 * }
 */
export interface ValidationError {
  fieldViolations?: FieldViolation[];
}

export enum RuleType {
  VALIDATION = 'VALIDATION',
  OTHER = 'OTHER',
  MAX = 'MAX',
  MIN = 'MIN',
  MAX_LENGTH = 'MAX_LENGTH',
  MIN_LENGTH = 'MIN_LENGTH',
  MAX_SIZE = 'MAX_SIZE',
  MIN_SIZE = 'MIN_SIZE',
  FORMAT = 'FORMAT',
  DECIMAL_LTE = 'DECIMAL_LTE',
  DECIMAL_GTE = 'DECIMAL_GTE',
  DECIMAL_LT = 'DECIMAL_LT',
  DECIMAL_GT = 'DECIMAL_GT',
  DECIMAL_MAX_SCALE = 'DECIMAL_MAX_SCALE',
  INVALID_ENUM_VALUE = 'INVALID_ENUM_VALUE',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  FIELD_NOT_ALLOWED = 'FIELD_NOT_ALLOWED',
  ONE_OF_ALIGNMENT = 'ONE_OF_ALIGNMENT',
  EXACT_LENGTH = 'EXACT_LENGTH',
  EXACT_SIZE = 'EXACT_SIZE',
  REQUIRED_ONE_OF_FIELD = 'REQUIRED_ONE_OF_FIELD',
}

/** @enumType */
export type RuleTypeWithLiterals =
  | RuleType
  | 'VALIDATION'
  | 'OTHER'
  | 'MAX'
  | 'MIN'
  | 'MAX_LENGTH'
  | 'MIN_LENGTH'
  | 'MAX_SIZE'
  | 'MIN_SIZE'
  | 'FORMAT'
  | 'DECIMAL_LTE'
  | 'DECIMAL_GTE'
  | 'DECIMAL_LT'
  | 'DECIMAL_GT'
  | 'DECIMAL_MAX_SCALE'
  | 'INVALID_ENUM_VALUE'
  | 'REQUIRED_FIELD'
  | 'FIELD_NOT_ALLOWED'
  | 'ONE_OF_ALIGNMENT'
  | 'EXACT_LENGTH'
  | 'EXACT_SIZE'
  | 'REQUIRED_ONE_OF_FIELD';

export interface FieldViolation {
  field?: string;
  description?: string;
  violatedRule?: RuleTypeWithLiterals;
  /** applicable when violated_rule=OTHER */
  ruleName?: string | null;
  data?: Record<string, any> | null;
}

export interface SystemError {
  /** Error code. */
  errorCode?: string | null;
}

export interface CarrierErrors {
  /** Carrier errors. */
  errors?: CarrierError[];
}

export interface CarrierError {
  /** Carrier ID. */
  carrierId?: string;
  /** Error details. */
  error?: Details;
}

export interface GiftCardCalculationError {
  /** Gift card code with most characters masked for security. */
  obfuscatedCode?: string;
  /** Error details. */
  error?: Details;
  /**
   * Full gift card code. Echoed from the request input for correlation.
   * @internal
   * @minLength 8
   * @maxLength 20
   */
  code?: string;
}

export enum WeightUnit {
  /** Weight unit can't be classified due to an error. */
  UNSPECIFIED_WEIGHT_UNIT = 'UNSPECIFIED_WEIGHT_UNIT',
  /** Kilograms. */
  KG = 'KG',
  /** Pounds. */
  LB = 'LB',
}

/** @enumType */
export type WeightUnitWithLiterals =
  | WeightUnit
  | 'UNSPECIFIED_WEIGHT_UNIT'
  | 'KG'
  | 'LB';

export interface MembershipOptions {
  /**
   * List of payment options that can be used.
   * Populated by the legacy ListEligibleMemberships flow.
   * @maxSize 300
   * @deprecated List of payment options that can be used.
   * Populated by the legacy ListEligibleMemberships flow.
   * @replacedBy validated_existing_memberships
   * @targetRemovalDate 2026-10-01
   */
  eligibleMemberships?: Membership[];
  /**
   * List of payment options that are owned by the member, but cannot be used due to reason provided.
   * Populated by the legacy ListEligibleMemberships flow.
   * @maxSize 300
   * @deprecated List of payment options that are owned by the member, but cannot be used due to reason provided.
   * Populated by the legacy ListEligibleMemberships flow.
   * @replacedBy validated_existing_memberships
   * @targetRemovalDate 2026-10-01
   */
  invalidMemberships?: InvalidMembership[];
  /**
   * The selected membership payment options and which line items they apply to.
   * Populated by the legacy ListEligibleMemberships flow.
   * @maxSize 300
   * @deprecated The selected membership payment options and which line items they apply to.
   * Populated by the legacy ListEligibleMemberships flow.
   * @replacedBy validated_existing_memberships
   * @targetRemovalDate 2026-10-01
   */
  selectedMemberships?: SelectedMembership[];
  /**
   * Validated existing memberships selected for redemption, each with the line items it can cover.
   * Populated by the EnhanceAndValidateSelection flow.
   * @internal
   */
  validatedExistingMemberships?: ValidatedExistingMemberships;
  /**
   * Validated future plans (purchasable during this checkout) and how their benefits cover the line items.
   * Populated by the EnhanceAndValidateSelection flow.
   * @internal
   */
  validatedPurchasablePlans?: ValidatedPurchasablePlans;
}

export interface Membership {
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 200
   */
  _id?: string;
  /**
   * ID of the application providing this payment option.
   * @format GUID
   */
  appId?: string;
  /** The name of this membership. */
  name?: MembershipName;
  /**
   * Deprecated. Use `ExistingMembership.covered_line_items` to see which line items this membership covers.
   *
   * Only populated in the legacy `ListEligibleMemberships` flow, which still requires it. Empty on
   * `ListMembershipsByIds`, a by-id lookup that receives no line items to report against.
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
  /** Membership's payment credits (balance information). */
  credits?: MembershipPaymentCredits;
  /** Membership expiration date. */
  expirationDate?: Date | null;
  /**
   * Deprecated. Use `offering_description`.
   * @deprecated
   * @replacedBy offering_description
   * @targetRemovalDate 2026-08-30
   */
  additionalData?: Record<string, any> | null;
  /**
   * Human-readable offering summary for display. Provider-supplied.
   * @internal
   * @maxLength 500
   */
  offeringDescription?: string | null;
}

export interface MembershipName {
  /**
   * Membership name.
   * @maxLength 100
   */
  original?: string;
  /**
   * Translated membership name. Defaults to `original` when not provided.
   * @maxLength 100
   */
  translated?: string | null;
}

export interface MembershipPaymentCredits {
  /**
   * Membership's initial value.
   * @min 1
   */
  total?: number;
  /** Membership's remaining value. */
  remaining?: number;
  /**
   * Deprecated. The new flow no longer exposes a per-item redemption cost on a membership's credits;
   * what a membership can pay for is conveyed by `ExistingMembership.covered_line_items` (per-item redemption cost).
   * @internal
   * @deprecated
   * @replacedBy ExistingMembership.covered_line_items.redemption_cost
   * @targetRemovalDate 2026-08-30
   */
  redemptionCost?: number;
  /**
   * Membership's balance type.
   * @internal
   */
  balanceType?: BalanceTypeWithLiterals;
}

export enum BalanceType {
  /** Balance is measured in discrete uses (e.g., sessions, visits). */
  PUNCH_CARD = 'PUNCH_CARD',
  /** Balance is measured in a currency-like value (e.g., points). */
  CREDIT = 'CREDIT',
}

/** @enumType */
export type BalanceTypeWithLiterals = BalanceType | 'PUNCH_CARD' | 'CREDIT';

export interface InvalidMembership {
  /** Membership details. */
  membership?: Membership;
  /**
   * Reason why this membership is invalid and cannot be used.
   * @minLength 1
   * @maxLength 100
   */
  reason?: string;
}

/** Same shape as `ListExistingMembershipsResponse.memberships`. */
export interface ValidatedExistingMemberships {
  /**
   * The selected memberships, each paired with the line items it can redeem and the cost to redeem each.
   * @maxSize 300
   */
  memberships?: ExistingMembership[];
}

/**
 * A membership the member already owns, together with the input line items it can pay for.
 *
 * A membership is a single benefit granted by a plan the member has already paid for, so coverage is
 * nested here per membership (no `benefit_id` linkage is needed, unlike a plan that grants many benefits).
 */
export interface ExistingMembership {
  /** The membership the member owns. */
  membership?: V1Membership;
  /**
   * The input line items this membership can pay for, and the cost to redeem each.
   * @maxSize 300
   */
  coveredLineItems?: CoveredLineItem[];
}

export interface V1Membership {
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 200
   */
  _id?: string;
  /** Membership name. */
  name?: MembershipName;
  /**
   * Deprecated. Use `ExistingMembership.covered_line_items` to see which line items this membership covers.
   *
   * Populate this only in the legacy `ListEligibleMemberships` flow, which still requires it and rejects a
   * membership that omits it. Leave it empty everywhere else - `ListMembershipsByIds` receives no line items
   * at all, so there is nothing to report there.
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   * @deprecated
   * @replacedBy ExistingMembership.covered_line_items.line_item_id
   * @targetRemovalDate 2026-08-30
   */
  lineItemIds?: string[];
  /** Membership's payment credits (balance information). */
  credits?: MembershipPaymentCredits;
  /** Membership expiration date. */
  expirationDate?: Date | null;
  /**
   * Deprecated. Use `offering_description` for human-readable provider data.
   * @deprecated
   * @replacedBy offering_description
   * @targetRemovalDate 2026-08-30
   */
  additionalData?: Record<string, any> | null;
  /**
   * Human-readable offering summary for display (for example price and validity text). Provider-supplied.
   * @internal
   * @maxLength 500
   */
  offeringDescription?: string | null;
}

/** A line item a membership can pay for, and the cost to redeem it. */
export interface CoveredLineItem {
  /**
   * The input line item this membership can cover.
   * @minLength 1
   * @maxLength 100
   */
  lineItemId?: string;
  /**
   * The amount to redeem from the membership's benefit quota (`benefit.limited_quota.remaining`) for this item.
   * Always 0 when the benefit is unlimited (nothing is decremented).
   */
  redemptionCost?: number;
}

/** Same shape as `ListPurchasablePlansResponse`. */
export interface ValidatedPurchasablePlans {
  /**
   * The selected plans.
   * @maxSize 300
   */
  purchasablePlans?: PurchasablePlan[];
  /**
   * How the plans' benefits cover the line items, linked to a benefit by `benefit_id`.
   * @maxSize 300
   */
  coverages?: LineItemCoverage[];
}

export interface PurchasablePlan {
  /** Catalog and item reference for the plan that can be purchased. */
  catalogReference?: CatalogReference;
  /** Plan name. */
  name?: MembershipName;
  /**
   * Benefits the plan would grant once purchased. Multiple entries support multi-benefit plans (e.g., a bundle granting separate yoga and pilates credits).
   * @minSize 1
   * @maxSize 50
   */
  benefits?: Benefit[];
  /**
   * Human-readable offering summary for display (for example price and validity text). Provider-supplied.
   * @maxLength 500
   */
  offeringDescription?: string | null;
  /**
   * URL the buyer is redirected to in order to collect additional info required to purchase this plan.
   * @format WEB_URL
   */
  infoCollectionUrl?: string | null;
  /**
   * Plan price.
   * @decimalValue options { gte:0, maxScale:3 }
   */
  price?: string;
  /**
   * Human-readable description of the plan price (for example "First month free, then $20/month").
   *
   * `price` is simply the price (useful for sorting or comparison, for example), while this is a
   * descriptive, display-oriented summary of it.
   * @maxLength 500
   */
  priceDescription?: string | null;
}

/**
 * A single redeemable benefit. A purchasable plan grants one or more benefits (`PurchasablePlan.benefits`);
 * once the plan is paid, each benefit becomes its own membership (charged via `membership_id`).
 */
export interface Benefit extends BenefitQuotaOneOf {
  limitedQuota?: LimitedQuota;
  /**
   * Identifier for this benefit. On a `PurchasablePlan` it must be unique within the response so
   * `coverages` (`LineItemCoverage.benefit_id`) can reference it unambiguously across the plan's benefits.
   * @minLength 1
   * @maxLength 200
   */
  benefitId?: string;
  /**
   * Human-readable label for this benefit (for example, "Manicure" or "Pedicure" on multi-set punchcards).
   * @maxLength 200
   */
  label?: string | null;
  /**
   * The kind of quota this benefit grants: a countable amount drawn down on redemption (`LIMITED`),
   * or an unbounded amount that is never drawn down (`UNLIMITED`).
   */
  quotaType?: QuotaTypeWithLiterals;
}

/** @oneof */
export interface BenefitQuotaOneOf {
  limitedQuota?: LimitedQuota;
}

export enum QuotaType {
  /** The benefit grants a countable quota that is drawn down on redemption. */
  LIMITED = 'LIMITED',
  /** The benefit is unlimited (unbounded) and is never drawn down. */
  UNLIMITED = 'UNLIMITED',
}

/** @enumType */
export type QuotaTypeWithLiterals = QuotaType | 'LIMITED' | 'UNLIMITED';

/** A limited, countable redeemable amount. */
export interface LimitedQuota {
  /**
   * The quota's initial value.
   * @min 1
   */
  total?: number;
  /** The quota's remaining value. */
  remaining?: number;
  /** How the quota is measured. */
  limitedQuotaType?: LimitedQuotaTypeWithLiterals;
}

export enum LimitedQuotaType {
  /** Quota is measured in discrete uses (e.g., sessions, visits). */
  PUNCH_CARD = 'PUNCH_CARD',
  /** Quota is measured in a currency-like value (e.g., points). */
  CREDIT = 'CREDIT',
}

/** @enumType */
export type LimitedQuotaTypeWithLiterals =
  | LimitedQuotaType
  | 'PUNCH_CARD'
  | 'CREDIT';

/**
 * Maps a benefit to a line item it can cover, and at what cost, linked to a benefit by `benefit_id`.
 * One entry per (benefit, line item). Returned by `ListPurchasablePlans` (alongside `purchasable_plans`).
 * For the existing-memberships flow the equivalent mapping is `ExistingMembership.covered_line_items`,
 * which needs no `benefit_id` because a membership is a single benefit.
 */
export interface LineItemCoverage {
  /**
   * The benefit that covers the line item, as returned in `Benefit.benefit_id`.
   * @minLength 1
   * @maxLength 200
   */
  benefitId?: string;
  /**
   * The line item this benefit covers.
   * @minLength 1
   * @maxLength 100
   */
  lineItemId?: string;
  /**
   * The amount to redeem from the benefit's `limited_quota.remaining` value for this item.
   * Always 0 for an unlimited benefit (nothing is decremented).
   */
  redemptionCost?: number;
}

export interface V1AdditionalFee {
  /**
   * Additional fee's unique code (or ID) for future processing.
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Additional fee's name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Additional fee's price. */
  price?: MultiCurrencyPrice;
  /** Tax details. */
  taxDetails?: ItemTaxFullDetails;
  /**
   * Provider's app id.
   * @minLength 1
   * @maxLength 100
   */
  providerAppId?: string | null;
  /** Additional fee's price before tax. */
  priceBeforeTax?: MultiCurrencyPrice;
  /** Additional fee's price after tax. */
  priceAfterTax?: MultiCurrencyPrice;
  /**
   * Optional - Line items associated with this additional fee.
   * If no `lineItemIds` are provided, the fee will be associated with the whole cart/checkout/order.
   * @format GUID
   */
  lineItemIds?: string[];
  /**
   * Number of subscription cycle this fee applies to
   * default None - all billing cycle
   * @internal
   * @min 1
   * @max 999
   */
  subscriptionCycles?: number | null;
  /** the source the additional fee was added from */
  source?: AdditionalFeeSourceWithLiterals;
  /**
   * The translated name of the additional fee. The translation language is determined by the `languages` field in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @minLength 1
   * @maxLength 50
   */
  translatedName?: string | null;
  /** Address type tax was calculated against for this fee. */
  taxableAddress?: TaxableAddress;
}

export enum AdditionalFeeSource {
  UNKNOWN_ADDITIONAL_FEE_SOURCE = 'UNKNOWN_ADDITIONAL_FEE_SOURCE',
  /** The additional fee was added by an additional fee service plugin */
  SERVICE_PLUGIN = 'SERVICE_PLUGIN',
  /** The additional fee was added on the item either via the catalog or on custom line item */
  ITEM = 'ITEM',
  /** The additional fee was added manually on the request */
  MANUAL = 'MANUAL',
  /** The additional fee was added by the shipping provider */
  SHIPPING = 'SHIPPING',
  /** The additional fee was added by a Wix vertical and represents a Wix platform fee */
  PLATFORM = 'PLATFORM',
}

/** @enumType */
export type AdditionalFeeSourceWithLiterals =
  | AdditionalFeeSource
  | 'UNKNOWN_ADDITIONAL_FEE_SOURCE'
  | 'SERVICE_PLUGIN'
  | 'ITEM'
  | 'MANUAL'
  | 'SHIPPING'
  | 'PLATFORM';

export interface PlatformFeeSummary {
  /** Total platform fee amount. */
  total?: MultiCurrencyPrice;
  /** Total platform fee passed on to the customer. */
  totalPassOn?: MultiCurrencyPrice;
  /** Total platform fee absorbed by the merchant. */
  totalAbsorbed?: MultiCurrencyPrice;
  /**
   * Calculated platform fees.
   * @maxSize 100
   */
  calculatedPlatformFees?: CalculatedPlatformFee[];
}

export interface CalculatedPlatformFee {
  /** Platform fee name. */
  name?: TranslatableString;
  /** Platform fee amount. */
  amount?: MultiCurrencyPrice;
  /**
   * ID of the line item the platform fee applies to.
   * @format GUID
   */
  lineItemId?: string;
  /** Platform fee charge type. */
  chargeType?: PlatformFeeChargeTypeWithLiterals;
  /**
   * Percentage rate charged as platform fee.
   * The fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:1, maxScale:4 }
   */
  percentageRate?: string;
}

/**
 * Returns a totals calculation for specified line items.
 * @public
 * @documentationMaturity preview
 * @requiredField options.lineItems._id
 * @requiredField options.lineItems.price
 * @requiredField options.lineItems.quantity
 * @requiredField options.selectedMemberships.memberships._id
 * @requiredField options.selectedMemberships.memberships.appId
 * @requiredField options.selectedMemberships.memberships.lineItemIds
 * @requiredField options.selectedShippingOption.code
 * @permissionId ECOM.CALCULATE_TOTALS
 * @applicableIdentity APP
 * @fqn com.wix.ecom.totals_calculator.v1.TotalsCalculator.CalculateTotals
 */
export async function calculateTotals(
  options?: NonNullablePaths<
    CalculateTotalsOptions,
    | `lineItems.${number}._id`
    | `lineItems.${number}.price`
    | `lineItems.${number}.quantity`
    | `selectedMemberships.memberships.${number}._id`
    | `selectedMemberships.memberships.${number}.appId`
    | `selectedMemberships.memberships.${number}.lineItemIds`
    | `selectedShippingOption.code`,
    3
  >
): Promise<
  NonNullablePaths<
    CalculateTotalsResponse,
    | `calculatedLineItems`
    | `calculatedLineItems.${number}.lineItemId`
    | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.amount`
    | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.convertedAmount`
    | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.formattedAmount`
    | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.formattedConvertedAmount`
    | `calculatedLineItems.${number}.pricesBreakdown.taxDetails.taxRate`
    | `calculatedLineItems.${number}.paymentOption`
    | `calculatedLineItems.${number}.taxableAddress.addressType`
    | `priceSummary.subtotal.amount`
    | `priceSummary.subtotal.convertedAmount`
    | `priceSummary.subtotal.formattedAmount`
    | `priceSummary.subtotal.formattedConvertedAmount`
    | `giftCard._id`
    | `giftCard.obfuscatedCode`
    | `giftCard.amount.amount`
    | `giftCard.amount.convertedAmount`
    | `giftCard.amount.formattedAmount`
    | `giftCard.amount.formattedConvertedAmount`
    | `giftCard.appId`
    | `taxSummary.taxableAmount.amount`
    | `taxSummary.taxableAmount.convertedAmount`
    | `taxSummary.taxableAmount.formattedAmount`
    | `taxSummary.taxableAmount.formattedConvertedAmount`
    | `taxSummary.calculationDetails.manualRateReason`
    | `taxSummary.calculationDetails.autoTaxFallbackDetails.fallbackReason`
    | `taxSummary.calculationDetails.autoTaxFallbackDetails.error.code`
    | `taxSummary.calculationDetails.autoTaxFallbackDetails.error.description`
    | `taxSummary.calculationDetails.rateType`
    | `shippingInfo.region._id`
    | `shippingInfo.region.name`
    | `shippingInfo.selectedCarrierServiceOption.code`
    | `shippingInfo.selectedCarrierServiceOption.title`
    | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address.streetAddress.number`
    | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address.streetAddress.name`
    | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.businessLocation`
    | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.pickupMethod`
    | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.amount`
    | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.convertedAmount`
    | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.formattedAmount`
    | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.formattedConvertedAmount`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxRate`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown.${number}.name`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown.${number}.rate`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxBreakdown`
    | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxBreakdown.${number}.jurisdictionType`
    | `shippingInfo.selectedCarrierServiceOption.requestedShippingOption`
    | `shippingInfo.selectedCarrierServiceOption.otherCharges`
    | `shippingInfo.selectedCarrierServiceOption.otherCharges.${number}.type`
    | `shippingInfo.selectedCarrierServiceOption.deliveryAllocations`
    | `shippingInfo.selectedCarrierServiceOption.deliveryAllocations.${number}.deliveryCarrier.code`
    | `shippingInfo.carrierServiceOptions`
    | `shippingInfo.carrierServiceOptions.${number}.carrierId`
    | `appliedDiscounts`
    | `appliedDiscounts.${number}.coupon._id`
    | `appliedDiscounts.${number}.coupon.code`
    | `appliedDiscounts.${number}.coupon.amount.amount`
    | `appliedDiscounts.${number}.coupon.amount.convertedAmount`
    | `appliedDiscounts.${number}.coupon.amount.formattedAmount`
    | `appliedDiscounts.${number}.coupon.amount.formattedConvertedAmount`
    | `appliedDiscounts.${number}.coupon.name`
    | `appliedDiscounts.${number}.discountRule._id`
    | `appliedDiscounts.${number}.discountRule.name.original`
    | `appliedDiscounts.${number}.discountType`
    | `calculationErrors.generalShippingCalculationError.applicationError.code`
    | `calculationErrors.generalShippingCalculationError.applicationError.description`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.field`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.description`
    | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.violatedRule`
    | `calculationErrors.carrierErrors.errors`
    | `calculationErrors.carrierErrors.errors.${number}.carrierId`
    | `calculationErrors.orderValidationErrors`
    | `weightUnit`
    | `currency`
    | `conversionCurrency`
    | `taxIncludedInPrice`
    | `payNow.subtotal.amount`
    | `payNow.subtotal.convertedAmount`
    | `payNow.subtotal.formattedAmount`
    | `payNow.subtotal.formattedConvertedAmount`
    | `payLater.subtotal.amount`
    | `payLater.subtotal.convertedAmount`
    | `payLater.subtotal.formattedAmount`
    | `payLater.subtotal.formattedConvertedAmount`
    | `membershipOptions.eligibleMemberships`
    | `membershipOptions.eligibleMemberships.${number}._id`
    | `membershipOptions.eligibleMemberships.${number}.appId`
    | `membershipOptions.eligibleMemberships.${number}.name.original`
    | `membershipOptions.eligibleMemberships.${number}.credits.total`
    | `membershipOptions.eligibleMemberships.${number}.credits.remaining`
    | `membershipOptions.invalidMemberships`
    | `membershipOptions.invalidMemberships.${number}.reason`
    | `membershipOptions.selectedMemberships`
    | `membershipOptions.selectedMemberships.${number}._id`
    | `membershipOptions.selectedMemberships.${number}.appId`
    | `additionalFees`
    | `additionalFees.${number}.name`
    | `additionalFees.${number}.price.amount`
    | `additionalFees.${number}.price.convertedAmount`
    | `additionalFees.${number}.price.formattedAmount`
    | `additionalFees.${number}.price.formattedConvertedAmount`
    | `additionalFees.${number}.taxDetails.taxRate`
    | `additionalFees.${number}.source`
    | `additionalFees.${number}.taxableAddress.addressType`
    | `payNowTotalAfterGiftCard.amount`
    | `payNowTotalAfterGiftCard.convertedAmount`
    | `payNowTotalAfterGiftCard.formattedAmount`
    | `payNowTotalAfterGiftCard.formattedConvertedAmount`
    | `totalAfterGiftCard.amount`
    | `totalAfterGiftCard.convertedAmount`
    | `totalAfterGiftCard.formattedAmount`
    | `totalAfterGiftCard.formattedConvertedAmount`
    | `payAfterFreeTrial.subtotal.amount`
    | `payAfterFreeTrial.subtotal.convertedAmount`
    | `payAfterFreeTrial.subtotal.formattedAmount`
    | `payAfterFreeTrial.subtotal.formattedConvertedAmount`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      calculationId: options?.calculationId,
      lineItems: options?.lineItems,
      shippingAddress: options?.shippingAddress,
      billingAddress: options?.billingAddress,
      selectedShippingOption: options?.selectedShippingOption,
      couponId: options?.couponId,
      couponCode: options?.couponCode,
      giftCardCode: options?.giftCardCode,
      buyerEmail: options?.buyerEmail,
      memberId: options?.memberId,
      selectedMemberships: options?.selectedMemberships,
      buyerPhone: options?.buyerPhone,
      purchaseFlowId: options?.purchaseFlowId,
      externalReferences: options?.externalReferences,
      businessLocationId: options?.businessLocationId,
      giftCardRedeemAmount: options?.giftCardRedeemAmount,
      contactId: options?.contactId,
      extendedFields: options?.extendedFields,
      taxExemptGroupId: options?.taxExemptGroupId,
      applyItemDiscountsToPrices: options?.applyItemDiscountsToPrices,
      giftCards: options?.giftCards,
      membershipSelection: options?.membershipSelection,
      sitePaymentMethodTypeId: options?.sitePaymentMethodTypeId,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: TRANSFORM_PATHS_0,
      },
    ]
  );

  const reqOpts = ambassadorWixEcomV1TotalsCalculation.calculateTotals(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: TRANSFORM_PATHS_1,
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          calculationId: '$[0].calculationId',
          lineItems: '$[0].lineItems',
          shippingAddress: '$[0].shippingAddress',
          billingAddress: '$[0].billingAddress',
          selectedShippingOption: '$[0].selectedShippingOption',
          couponId: '$[0].couponId',
          couponCode: '$[0].couponCode',
          giftCardCode: '$[0].giftCardCode',
          buyerEmail: '$[0].buyerEmail',
          memberId: '$[0].memberId',
          selectedMemberships: '$[0].selectedMemberships',
          buyerPhone: '$[0].buyerPhone',
          purchaseFlowId: '$[0].purchaseFlowId',
          externalReferences: '$[0].externalReferences',
          businessLocationId: '$[0].businessLocationId',
          giftCardRedeemAmount: '$[0].giftCardRedeemAmount',
          contactId: '$[0].contactId',
          extendedFields: '$[0].extendedFields',
          taxExemptGroupId: '$[0].taxExemptGroupId',
          applyItemDiscountsToPrices: '$[0].applyItemDiscountsToPrices',
          giftCards: '$[0].giftCards',
          membershipSelection: '$[0].membershipSelection',
          sitePaymentMethodTypeId: '$[0].sitePaymentMethodTypeId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CalculateTotalsOptions
  extends CalculateTotalsOptionsCouponOneOf,
    CalculateTotalsOptionsGiftCardOneOf {
  /**
   * Calculation ID. An identifier of the entity that you are calculating totals for, for example, cart ID or checkout ID.
   * This value will be used in order to optimise calculations by caching.
   * If not passed, calculation might be slower in some cases.
   * @minLength 1
   * @maxLength 100
   */
  calculationId?: string | null;
  /**
   * Line items for calculation.
   * @maxSize 300
   */
  lineItems?: LineItem[];
  /** Shipping address for tax and shipping calculation (if applicable). */
  shippingAddress?: Address;
  /** Billing address. Used for calculating tax if all the items in the cart are not shippable. */
  billingAddress?: Address;
  /** Details about selected shipping option. */
  selectedShippingOption?: SelectedShippingOption;
  /** Coupon ID. */
  couponId?: string | null;
  /** Coupon code. */
  couponCode?: string | null;
  /**
   * DEPRECATED: Use gift_cards instead.
   * @deprecated DEPRECATED: Use gift_cards instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardCode?: string | null;
  /**
   * Buyer email. Used to enforce coupon [single-use per customer](https://support.wix.com/en/article/wix-stores-limiting-coupon-usage). If not passed, single coupon usage per customer will not be enforced.
   * @format EMAIL
   */
  buyerEmail?: string | null;
  /**
   * Member id to use for this calculation
   * When not provided, given that the caller is a member, its member id will be used
   * Providing this require elevated permissions - ECOM.ADMIN_CALCULATE_TOTALS
   */
  memberId?: string | null;
  /**
   * The selected memberships and which line items they apply to
   * When not provided, default selection will be returned
   * You can also set it to empty selection
   * Mutually exclusive with `membership_selection` - providing both returns an INVALID_ARGUMENT error.
   */
  selectedMemberships?: SelectedMemberships;
  /** Buyer phone number. Used to get shipping rates. */
  buyerPhone?: string | null;
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   */
  purchaseFlowId?: string | null;
  /**
   * Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.
   * For example, a Wix Pay Link ID.
   * @maxSize 20
   */
  externalReferences?: ExternalReference[];
  /**
   * The business location ID to be used as the basis for some calculations.
   * Learn more about the [Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).
   * @format GUID
   */
  businessLocationId?: string | null;
  /**
   * DEPRECATED: Use `giftCards.redeemAmount` instead.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   * @deprecated DEPRECATED: Use `giftCards.redeemAmount` instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardRedeemAmount?: string | null;
  /**
   * Buyer contact ID.
   * @internal
   * @format GUID
   */
  contactId?: string | null;
  /** [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields). */
  extendedFields?: ExtendedFields;
  /**
   * Exempt group to use for tax calculations.
   * @internal
   * @format GUID
   */
  taxExemptGroupId?: string | null;
  /**
   * Whether SPECIFIC_ITEMS discounts are applied directly to item prices.
   *
   * When `true`:
   * + SPECIFIC_ITEMS discounts reduce item `price`, which is reflected in the subtotal.
   * + GLOBAL discounts are applied on top of the subtotal, without modifying item `price`.
   *
   * When `false` (current behavior):
   * + All coupons and merchant discounts are treated as GLOBAL; item prices are unchanged.
   *
   * Note: SPECIFIC_ITEMS automatic discounts are already applied to item prices, regardless of this setting.
   *
   * Default: `false`
   * @internal
   */
  applyItemDiscountsToPrices?: boolean;
  /**
   * Gift cards to apply.
   * @internal
   * @maxSize 5
   */
  giftCards?: GiftCardInput[];
  /**
   * Membership selection (existing + future) to be validated and enhanced.
   * When set, memberships are resolved via the EnhanceAndValidateSelection SPI flow.
   * Mutually exclusive with `selected_memberships` - providing both returns an INVALID_ARGUMENT error.
   * @internal
   */
  membershipSelection?: MembershipSelection;
  /**
   * ID of the site payment method type the customer selected to pay with. See the [Site Payment Method Types API](https://dev.wix.com/docs/api-reference/business-management/payments/site-payment-method-types/introduction).
   * @internal
   * @format GUID
   */
  sitePaymentMethodTypeId?: string | null;
}

/** @oneof */
export interface CalculateTotalsOptionsCouponOneOf {
  /** Coupon ID. */
  couponId?: string | null;
  /** Coupon code. */
  couponCode?: string | null;
}

/** @oneof */
export interface CalculateTotalsOptionsGiftCardOneOf {
  /**
   * DEPRECATED: Use gift_cards instead.
   * @deprecated DEPRECATED: Use gift_cards instead.
   * @replacedBy gift_cards
   * @targetRemovalDate 2026-12-01
   */
  giftCardCode?: string | null;
}
